import { memo, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { Spin, Input, Button, Avatar, Upload, Alert, Modal } from 'antd';
import {
  UserOutlined,
  PaperClipOutlined,
  SendOutlined,
  FileOutlined,
  MessageOutlined,
  MinusOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth, useNotification } from '../../../contexts';
import {
  useLazyGetChatAttachmentFileQuery,
  type ChatMessage,
} from '../../../store/rtkQuery/chat';
import { io, Socket } from 'socket.io-client';
import config from '../../../config';
import { useGetAllUsersQuery, useGetProfilePictureQuery } from '../../../store';
import { SystemModule } from '../../../enums';

const { TextArea } = Input;
const CHAT_WIDGET_POSITION_KEY = 'activity_chat_widget_position';

type FloatingPosition = {
  left: number;
  top: number;
};

const clampFloatingPosition = (
  left: number,
  top: number,
  width: number,
  height: number,
): FloatingPosition => ({
  left: Math.min(Math.max(8, left), Math.max(8, window.innerWidth - width - 8)),
  top: Math.min(Math.max(8, top), Math.max(8, window.innerHeight - height - 8)),
});

const readSavedFloatingPosition = (): FloatingPosition | null => {
  try {
    const value = localStorage.getItem(CHAT_WIDGET_POSITION_KEY);
    if (!value) return null;

    const parsed = JSON.parse(value) as FloatingPosition;
    if (typeof parsed.left !== 'number' || typeof parsed.top !== 'number') {
      return null;
    }

    return {
      left: Math.min(Math.max(8, parsed.left), Math.max(8, window.innerWidth - 56)),
      top: Math.min(Math.max(8, parsed.top), Math.max(8, window.innerHeight - 56)),
    };
  } catch {
    return null;
  }
};

interface ChatProps {
  requestNo: string;
  header?: string;
  module: string;
  subContractNo?: string
}
interface messageProps {
  msg?: any;
  isMine?: boolean;
  senderName?: string;
  time?: string;
  isImage?: boolean;
}
const UserAvatar = memo(
  ({ userId, name, size = 24, bg = 'bg-[#E2E8F0]', text = '#FFFFFF', disableFetch = false }: any) => {
    const { data, isSuccess } = useGetProfilePictureQuery(userId, {
      skip: !userId || disableFetch,
    });

    const [imgSrc, setImgSrc] = useState<string | null>(null);

    useEffect(() => {
      if (isSuccess && data) {
        const url = URL.createObjectURL(data);
        setImgSrc(url);
        // Cleanup: Revoke the URL when component unmounts or data changes
        return () => URL.revokeObjectURL(url);
      }
    }, [data, isSuccess]);

    return (
      <Avatar
        size={size}
        src={imgSrc}
        className={`${bg} text-[6px] font-medium ${text}`}
      >
        {!imgSrc && (name || 'U')[0]?.toUpperCase()}
      </Avatar>
    );
  },
);
export function Chat({
  requestNo,
  subContractNo,
  header = 'Request Chat',
  module,
}: ChatProps) {
  const [errorComing, setErrorComing] = useState(false);
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [message, setMessage] = useState('');
  const chatBodyRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [chatPosition, setChatPosition] = useState<FloatingPosition | null>(
    readSavedFloatingPosition,
  );
  const suppressChatClickRef = useRef(false);
  const chatDragRef = useRef({
    isDragging: false,
    moved: false,
    offsetX: 0,
    offsetY: 0,
    position: null as FloatingPosition | null,
  });
  const [roomId, setRoomId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [liveMessages, setLiveMessages] = useState<ChatMessage[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [addingMemberId, setAddingMemberId] = useState<number | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newMemberId, setNewMemberId] = useState<number | null>(null);
  const { data: allUsersResponse, isFetching: isLoadingUsers } = useGetAllUsersQuery(undefined, {
    skip: !isChatOpen && !isMemberModalOpen,
  });

  const apiBase = useMemo(() => {
    const base =
      (import.meta.env.VITE_ENV_HOST_URL as string | undefined) ||
      (config as any)?.urls?.host ||
      window.location.origin;
    return String(base).replace(/\/$/, '');
  }, []);

  const messages = useMemo(() => liveMessages, [liveMessages]);

  const scrollToBottom = () => {
    const el = chatBodyRef.current;
    if (!el) return;
    // Run after layout settles (initial render + message append)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  };

  const loadMoreMessages = () => {
    if (!socketRef.current || !roomId || isLoadingMore) return;

    setIsLoadingMore(true);

    const nextPage = page + 1;

    socketRef.current.emit('getMessages', {
      roomId,
      userId: user.ID,
      page: nextPage,
      pageSize: 50,
    });

    setPage(nextPage);
  };
  useEffect(() => {
    if (!requestNo || !user?.ID) return;
    setIsConnecting(true);
    setRoomId(null);
    setLiveMessages([]);

    const apiBase =
      (import.meta.env.VITE_ENV_HOST_URL as string | undefined) ||
      (config as any)?.urls?.host ||
      window.location.origin;
    const socket = io(String(apiBase).replace(/\/$/, ''), {
      transports: ['websocket'],
      withCredentials: true,
    });

    socketRef.current = socket;
    // socket.onAny((event, ...args) => {
    //   console.log('Socket event received:', event, args);
    // });
    socket.on('connect', () => {
      socket.emit('getRoomByRequestNo', { requestNo, subContractNo, userId: user.ID, module });
    });

    socket.on('roomReady', (data: any) => {
      const rid = data?.room?.id;
      if (rid) {
        setRoomId(rid);
        socket.emit('getMessages', {
          roomId: rid,
          userId: user.ID,
          page: 1,
          pageSize: 50,
        });
        setPage(1);
        setHasMore(true);
        socket.emit('getMembers', { roomId: rid });
      }
      setIsConnecting(false);
    });


    socket.on('messages', (data: any) => {
      const { list, count, page: currentPage, pageSize } = data;

      if (Array.isArray(list)) {
        setLiveMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = list.filter(m => !existingIds.has(m.id));

          return currentPage === 1
            ? newMessages
            : [...newMessages, ...prev];
        });

        setHasMore(currentPage * pageSize < count);
        setIsLoadingMore(false);
      }
    });

    socket.on('members', (data: any) => {
      const list = data?.members;

      if (Array.isArray(list)) {
        setMembers(prev => {
          const newMember = list.find(
            m => !prev.some(p => p.userId === m.userId),
          );

          if (newMember) {
            setNewMemberId(newMember.userId);

            setTimeout(() => setNewMemberId(null), 3000);
          }

          return list;
        });
      }
    });

    socket.on('messageCreated', (msg: ChatMessage) => {
      setLiveMessages(prev => {
        const exists = prev.find(m => m.id === msg.id);
        if (exists) return prev;

        return [...prev, msg];
      });

      if (msg.senderId === user?.ID) return;

      if (!isChatOpenRef.current) {
        setUnreadCount(prev => prev + 1);
      }
    });

    socket.on('messagesRead', ({ userId, messageIds }) => {
      setLiveMessages(prev =>
        prev.map(msg =>
          messageIds.includes(msg.id)
            ? {
              ...msg,
              readBy: [...(msg.readBy || []), userId],
            }
            : msg
        )
      );
    });

    socket.on('chatError', (e: any) => {
      // showNotification('error', 'Error', `${e?.message || 'Chat error'}`);
      console.log(e?.message);
      setIsConnecting(false);
      setIsSending(false);
    });

    socket.on('connect_error', (err: any) => {
      // showNotification(
      //   'error',
      //   'Error',
      //   `${err?.message || 'Failed to connect to chat server'}`,
      // );
      console.log(err?.message);
      setIsConnecting(false);
    });

    const timeout = setTimeout(() => {
      setIsConnecting(false);
    }, 10000);

    return () => {
      clearTimeout(timeout);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [requestNo, subContractNo, user?.ID]);

  useEffect(() => {
    if (!isChatOpenRef.current) {
      const count = messages.filter(
        (msg) => msg.senderId !== user?.ID && !msg.readBy?.includes(user?.ID)
      ).length;
      setUnreadCount(count);
    }
  }, [messages, user?.ID]);

  useEffect(() => {
    if (isChatOpen && roomId && user?.ID) {
      setUnreadCount(0);

      socketRef.current?.emit('markAsRead', {
        roomId,
        userId: user.ID,
      });
    }
  }, [isChatOpen, roomId]);
  const isChatOpenRef = useRef(isChatOpen);

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);
  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  useEffect(() => {
    if (isChatOpen) {
      scrollToBottom();
    }
  }, [isChatOpen]);

  const handleSend = async () => {
    if (!roomId || !message.trim()) return;
    const content = message.trim();
    setMessage('');

    if (socketRef.current && user?.ID) {
      setIsSending(true);
      socketRef.current.emit('sendMessage', {
        roomId,
        userId: user.ID,
        content,
      });
      setTimeout(() => setIsSending(false), 1500);
    } else {
      showNotification('error', 'Error', `Chat socket is not connected`);
    }
  };
  const handleUpload = async (file: File) => {
    setIsSending(true);
    if (!roomId || !user?.ID) {
      showNotification('error', 'Error', `Chat room is not ready`);

      setIsSending(false);
    } else {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${apiBase}/chat/rooms/${roomId}/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!res.ok) {
          setIsSending(false);
          throw new Error('Upload failed');
        }
        const json = await res.json();
        const data = json?.data;

        if (socketRef.current && (data?.fileName || data?.filePath)) {
          socketRef.current.emit('sendMessage', {
            roomId,
            userId: user.ID,
            content: null,
            filePath: data?.filePath,
            fileName: data?.fileName,
            mimeType: data?.mimeType,
          });
          setTimeout(() => setIsSending(false), 1500);
        }
      } catch (err: any) {
        setIsSending(false);
        showNotification(
          'error',
          'Error',
          `${err?.message || 'Failed to upload attachment'}`,
        );
      }
    }

    return false;
  };

  const meId = user?.ID;
  const availableUsers = useMemo(() => {
    const allUsers = (allUsersResponse as any)?.data ?? [];
    if (!allUsers.length) return [];
    const memberIds = new Set(members.map((m: any) => m.userId));
    return allUsers.filter((u: any) => !memberIds.has(u.ID));
  }, [allUsersResponse, members]);

  const memberListRef = useRef<HTMLDivElement | null>(null);
  const handleAddMember = (memberUserId: number) => {
    if (!socketRef.current || !roomId) return;
    setAddingMemberId(memberUserId);
    socketRef.current.emit('addMember', { roomId, memberUserId, addedByUserId: user.ID });
    setTimeout(() => setAddingMemberId(null), 1500);
  };

  const handleRemoveMember = (memberUserId: number) => {
    if (!socketRef.current || !roomId) return;
    setRemovingMemberId(memberUserId);
    socketRef.current.emit('removeMember', { roomId, memberUserId, removedByUserId: user.ID });
    setTimeout(() => setRemovingMemberId(null), 1500);
  };
  useEffect(() => {
    if (memberListRef.current) {
      memberListRef.current.scrollTop = memberListRef.current.scrollHeight;
    }
  }, [members]);
  const [userSearch, setUserSearch] = useState('');
  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return availableUsers.slice(0, 30);
    const q = userSearch.toLowerCase();
    return availableUsers
      .filter(
        (u: any) =>
          (u.FullName || '').toLowerCase().includes(q) ||
          (u.EmployeeCode || '').toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [availableUsers, userSearch]);

  const [getChatAttachmentFile] = useLazyGetChatAttachmentFileQuery();

  const { showNotification } = useNotification();
  const [pdfViewerState, setPdfViewerState] = useState<{
    id: string;
    isOpen: boolean;
    pdfUrl: string | null;
    fileName: string;
  }>({
    id: null,
    isOpen: false,
    pdfUrl: null,
    fileName: '',
  });
  const [triggerDownload] = useLazyGetChatAttachmentFileQuery();

  const handleChatPointerDown = (
    event: PointerEvent<HTMLElement>,
  ) => {
    if (event.button !== 0) return;

    const target = event.target as HTMLElement | null;
    if (
      target &&
      target.closest(
        '.chat-no-drag, input, textarea, a, button:not(#chat-floating-btn)',
      )
    ) {
      return;
    }

    const container = chatContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    chatDragRef.current = {
      isDragging: true,
      moved: false,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      position: null,
    };
    if (event.currentTarget && event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch { }
    }
  };

  const handleChatPointerMove = (
    event: PointerEvent<HTMLElement>,
  ) => {
    const dragState = chatDragRef.current;
    const container = chatContainerRef.current;
    if (!dragState.isDragging || !container) return;

    const currentWidth = isChatOpen ? 360 : 48;
    const currentHeight = isChatOpen ? 450 : 48;

    const nextPosition = clampFloatingPosition(
      event.clientX - dragState.offsetX,
      event.clientY - dragState.offsetY,
      currentWidth,
      currentHeight,
    );

    if (!dragState.moved) {
      const rect = container.getBoundingClientRect();
      dragState.moved =
        Math.abs(nextPosition.left - rect.left) > 3 ||
        Math.abs(nextPosition.top - rect.top) > 3;
    }

    if (dragState.moved) {
      suppressChatClickRef.current = true;
      dragState.position = nextPosition;
      setChatPosition(nextPosition);
    }
  };

  const handleChatPointerUp = (event: PointerEvent<HTMLElement>) => {
    const dragState = chatDragRef.current;
    const wasMoved = dragState.moved;
    const nextPosition = dragState.position;
    dragState.isDragging = false;

    if (
      event.currentTarget &&
      event.currentTarget.hasPointerCapture &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch { }
    }

    if (wasMoved && nextPosition) {
      const positionToSave = isChatOpen
        ? {
            left: Math.min(nextPosition.left, window.innerWidth - 48 - 8),
            top: Math.min(nextPosition.top + 450 - 48, window.innerHeight - 48 - 8),
          }
        : nextPosition;

      localStorage.setItem(
        CHAT_WIDGET_POSITION_KEY,
        JSON.stringify(positionToSave),
      );
      if (isChatOpen) {
        setChatPosition(positionToSave);
      }
      window.setTimeout(() => {
        suppressChatClickRef.current = false;
      }, 0);
    } else {
      suppressChatClickRef.current = false;
    }
  };

  const handleChatPointerCancel = (
    event: PointerEvent<HTMLElement>,
  ) => {
    chatDragRef.current.isDragging = false;
    if (
      event.currentTarget &&
      event.currentTarget.hasPointerCapture &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch { }
    }
  };

  const handleViewAttachment = async (record: any) => {
    try {
      const attachmentIdToUse = record.id || record.attachmentId;
      const fileName = record.fileName || `attachment-${attachmentIdToUse}`;

      if (!attachmentIdToUse) {
        showNotification('error', 'Error', 'Required IDs are missing');
        return;
      }

      const response = await getChatAttachmentFile({
        attachmentId: String(attachmentIdToUse),
      }).unwrap();

      const blobUrl = URL.createObjectURL(response);
      setPdfViewerState({
        id: record.id,
        isOpen: true,
        pdfUrl: blobUrl,
        fileName: fileName,
      });
    } catch (error) {
      showNotification('error', 'Error', 'Failed to load file');
    }
  };

  const handleDownloadAttachment = async (record: any) => {
    try {
      if (!record) {
        showNotification('error', 'Error', 'Invalid record provided');
        return;
      }

      const attachmentIdToUse = record.id || record.attachmentId;
      const fileName =
        record.fileName || `attachment-${attachmentIdToUse || 'unknown'}`;

      if (!attachmentIdToUse) {
        showNotification('error', 'Error', 'Required IDs are missing');
        return;
      }

      const response = await triggerDownload({
        attachmentId: String(attachmentIdToUse),
      }).unwrap();

      if (response) {
        const blobUrl = URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        showNotification('success', 'Success', 'File downloaded successfully');
      }
    } catch (error) {
      showNotification('error', 'Error', 'Failed to download file');
    }
  };

  const MessageBubble = ({
    msg,
    isMine,
    senderName,
    time,
    isImage,
  }: messageProps) => {
    return (
      <div className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
        {/* NAME */}
        <div
          className={`text-[7px] mb-[2px] font-medium ${isMine ? 'text-blue-100 text-right' : 'text-[#94A3B8]'
            }`}
        >
          {isMine ? 'You' : senderName}
        </div>

        {/* BUBBLE */}
        <div
          className={`px-2 py-0.5 rounded-xl text-[9px] shadow-sm max-w-[200px] ${isMine
            ? 'bg-[#2563EB] text-white rounded-br-sm'
            : 'bg-[#F1F5F9] text-[#334155] rounded-bl-sm'
            }`}
        >
          {/* TEXT */}
          {msg.content && (
            <div
              className={`flex items-end gap-1 ${isMine ? 'justify-end' : ''}`}
            >
              <span className="whitespace-pre-wrap break-words leading-snug">
                {msg.content}
              </span>

              <span
                className={`text-[7px] whitespace-nowrap ${isMine ? 'text-blue-100' : 'text-[#94A3B8]'
                  }`}
              >
                {time}
              </span>
            </div>
          )}

          {/* IMAGE */}
          {msg.fileName && isImage && (
            <div className="relative mt-1 inline-block">
              <img
                onClick={() => handleViewAttachment(msg)}
                src={`${apiBase}/chat/files/${encodeURIComponent(
                  msg.fileName,
                )}`}
                className={`rounded-md max-h-32 border cursor-pointer ${isMine ? 'border-blue-400' : 'border-[#E2E8F0]'
                  }`}
              />

              {/* TIME OVERLAY */}
              <span className="absolute bottom-1 right-1 text-[7px] text-white bg-black/40 px-1 rounded">
                {time}
              </span>
            </div>
          )}

          {/* FILE */}
          {msg.fileName && !isImage && (
            <div
              className={`relative mt-1 flex items-center gap-1 cursor-pointer pr-8 ${isMine ? 'text-blue-200 justify-end' : 'text-blue-600'
                }`}
              onClick={() => handleViewAttachment(msg)}
            >
              <FileOutlined />

              <span className="truncate max-w-[120px]">{msg.fileName}</span>

              {/* TIME */}
              <span
                className={`absolute bottom-0 right-0 text-[7px] ${isMine ? 'text-blue-100' : 'text-[#94A3B8]'
                  }`}
              >
                {time}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const containerStyle = useMemo(() => {
    if (!chatPosition) {
      return { zIndex: 999999 };
    }

    if (!isChatOpen) {
      return {
        left: chatPosition.left,
        top: chatPosition.top,
        right: 'auto',
        bottom: 'auto',
        zIndex: 999999,
      };
    }

    const windowWidth = 360;
    const windowHeight = 450;
    const buttonHeight = 48;

    const openUpwards =
      chatPosition.top + windowHeight > window.innerHeight - 8;
    let top = openUpwards
      ? chatPosition.top - windowHeight + buttonHeight
      : chatPosition.top;

    top = Math.min(
      Math.max(8, top),
      Math.max(8, window.innerHeight - windowHeight - 8),
    );

    let left = chatPosition.left;
    if (left + windowWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - windowWidth - 8);
    } else {
      left = Math.max(8, left);
    }

    return {
      left,
      top,
      right: 'auto',
      bottom: 'auto',
      zIndex: 999999,
    };
  }, [chatPosition, isChatOpen]);

  return (
    <>
      <div
        id="chat-container"
        ref={chatContainerRef}
        style={containerStyle}
      >
        {!isChatOpen ? (
          <>
            <button
              id="chat-floating-btn"
              onPointerDown={handleChatPointerDown}
              onPointerMove={handleChatPointerMove}
              onPointerUp={handleChatPointerUp}
              onPointerCancel={handleChatPointerCancel}
              onClick={event => {
                if (suppressChatClickRef.current) {
                  event.preventDefault();
                  return;
                }
                if (!isChatOpen) {
                  setIsChatOpen(true);
                }
              }}
              className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 text-white flex items-center justify-center shadow-lg relative cursor-move touch-none"
            >
              <MessageOutlined />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="w-[360px] h-[450px] rounded-xl shadow-lg bg-white flex flex-col overflow-hidden border border-slate-200">
              {/* HEADER */}
              <div
                onPointerDown={handleChatPointerDown}
                onPointerMove={handleChatPointerMove}
                onPointerUp={handleChatPointerUp}
                onPointerCancel={handleChatPointerCancel}
                className="flex items-center justify-between px-4 py-3 bg-white border-b border-[#EEF2F6] cursor-move touch-none select-none"
              >
                <div>
                  <div className="text-[8px] text-[#94A3B8] font-semibold tracking-wide">
                    PROJECT ID
                  </div>
                  <div className="text-[14px] font-semibold text-[#0F172A]">
                    {requestNo}
                  </div>
                </div>
                {module == SystemModule.CONTRACT_MANAGEMENT &&
                  <div>
                    <div className="text-[8px] text-[#94A3B8] font-semibold tracking-wide">
                      SubContract  No.
                    </div>
                    <div className="text-[14px] font-semibold text-[#0F172A]">
                      {subContractNo}
                    </div>
                  </div>}

                {/* MEMBERS (STACKED AVATARS) */}
                <div className="flex items-center gap-2">
                  <div
                    onClick={() => setIsMemberModalOpen(true)}
                    className="flex items-center bg-[#F1F5F9] hover:bg-[#E2E8F0] px-2 py-1 rounded-full cursor-pointer chat-no-drag"
                  >
                    <div className="flex -space-x-2">
                      {members
                        .slice(0, 4)
                        ?.map((m, i) => (
                          <UserAvatar
                            userId={m?.user?.ID}
                            name={m?.user?.FullName}
                          />
                        ))}
                    </div>

                    <span className="ml-2 text-[9px] text-[#475569]">
                      {members.length} members <UserAddOutlined />
                    </span>
                  </div>

                  <Button
                    size="small"
                    type="text"
                    onClick={() => {
                      setIsChatOpen(false);
                      setIsMemberModalOpen(false);
                    }}
                    icon={<MinusOutlined />}
                    className="text-[#64748B] chat-no-drag"
                  />
                </div>
              </div>
              {(isConnecting || errorComing) ? (
                <div className="flex justify-center py-2">
                  <Spin size="small" />
                  <span className="ml-2 text-xs text-gray-500">
                    Connecting...
                  </span>
                </div>
              ) : (
                <>
                  {/* CHAT BODY */}
                  <div
                    ref={chatBodyRef}
                    className="flex-1 overflow-y-auto px-3 py-3 bg-[#F8FAFC] space-y-3"
                  >
                    {hasMore && (
                      <div className="flex justify-center">
                        <button
                          onClick={loadMoreMessages}
                          disabled={isLoadingMore}
                          className="px-3 py-1 text-[9px] bg-gray-100 rounded-full hover:bg-gray-200"
                        >
                          {isLoadingMore ? 'Loading...' : 'Load older messages'}
                        </button>
                      </div>
                    )}
                    {messages.map(msg => {
                      const isMine = msg.senderId === meId;
                      const time = dayjs(msg.createdAt).format('HH:mm');
                      const senderName = msg.sender?.FullName || 'User';

                      const isImage = /\.(png|jpe?g|gif|webp)$/i.test(
                        (msg.fileName || '').toLowerCase(),
                      );

                      // System message — centered grey pill like WhatsApp
                      if (msg.isSystem) {
                        return (
                          <div key={msg.id} className="flex justify-center my-1">
                            <span className="text-[8px] text-[#94A3B8] bg-[#F1F5F9] px-3 py-[3px] rounded-full">
                              {msg.content}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={msg.id}
                          className={`flex w-full ${isMine ? 'justify-end' : 'justify-start'
                            }`}
                        >
                          {!isMine && (
                            <div className="flex items-end gap-1 max-w-[75%]">
                              <UserAvatar
                                userId={msg.senderId}
                                name={senderName}
                              />

                              <MessageBubble
                                msg={msg}
                                isMine={false}
                                senderName={senderName}
                                time={time}
                                isImage={isImage}
                              />
                            </div>
                          )}

                          {isMine && (
                            <div className="flex items-end gap-1 max-w-[75%]">
                              <MessageBubble
                                msg={msg}
                                isMine={true}
                                senderName={senderName}
                                time={time}
                                isImage={isImage}
                              />

                              <UserAvatar userId={meId} name={senderName} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* INPUT */}
                  <div className="border-t border-[#EEF2F6] bg-white px-3 py-2">
                    <div className="flex items-end gap-2 bg-[#F8FAFC] rounded-lg px-2 py-1 border border-[#E2E8F0]">
                      <Upload
                        beforeUpload={handleUpload}
                        showUploadList={false}
                      >
                        <Button
                          icon={<PaperClipOutlined />}
                          type="text"
                          className="text-[#64748B]"
                        />
                      </Upload>

                      <TextArea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        placeholder="Type a message..."
                        bordered={false}
                        className="bg-transparent text-[10px]"
                        onPressEnter={e => {
                          if (!e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                      />

                      <Button
                        type="primary"
                        icon={
                          isSending ? <Spin size="small" /> : <SendOutlined />
                        }
                        onClick={handleSend}
                        disabled={!message.trim() || isSending}
                        className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white border-none"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <Modal
        open={isMemberModalOpen}
        onCancel={() => setIsMemberModalOpen(false)}
        footer={null}
        width={340}
        centered
        closable
        destroyOnClose={false}
        zIndex={9999}
        title={
          <div className="text-sm font-semibold text-slate-800 tracking-tight">
            Chat Members
          </div>
        }
        styles={{ body: { padding: '10px 12px 14px' } }}
      >
        {/* CURRENT MEMBERS */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5 px-0.5">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              Current Members
            </span>
            <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              {members.length}
            </span>
          </div>

          {members.length === 0 ? (
            <div className="text-[11px] text-slate-400 py-2 text-center bg-slate-50 rounded-lg">
              No members yet.
            </div>
          ) : (
            <div
              ref={memberListRef}
              className="max-h-[140px] overflow-y-auto space-y-1 pr-1"
            >
              {members.map(m => {
                const isNew = m.userId === newMemberId;
                const isMe = m.userId === meId;

                return (
                  <div
                    key={m.id}
                    className={`flex items-center justify-between py-1 px-2 rounded-lg transition-all duration-300 ${isNew ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50'
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <UserAvatar
                        userId={m?.user?.ID}
                        name={m?.user?.FullName}
                        size={26}
                        bg="bg-blue-600"
                      />
                      <div className="leading-tight min-w-0">
                        <div className="text-[11px] font-medium text-slate-800 truncate">
                          {m.user?.FullName || m.userId}
                        </div>
                        <div className="text-[9px] text-slate-400 flex items-center gap-1">
                          {isMe ? (
                            <span className="text-blue-600 font-semibold bg-blue-50 px-1 rounded">You</span>
                          ) : (
                            <span>Member</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {!isMe && (
                      <button
                        onClick={() => handleRemoveMember(m.userId)}
                        disabled={removingMemberId === m.userId}
                        className={`text-[10px] font-medium px-2 py-0.5 rounded transition-colors border-none cursor-pointer ${removingMemberId === m.userId
                          ? 'opacity-50 cursor-not-allowed text-slate-400'
                          : 'text-slate-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                      >
                        {removingMemberId === m.userId ? '...' : 'Remove'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <hr className="border-slate-100 my-2" />

        {/* ADD MEMBERS */}
        <div>
          <div className="mb-1.5 px-0.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
            Add Members
          </div>

          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-4">
              <Spin size="small" />
              <span className="ml-2 text-[11px] text-slate-400">Loading users...</span>
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="text-[11px] text-slate-400 py-2 text-center bg-slate-50 rounded-lg">
              No more users to add.
            </div>
          ) : (
            <>
              {/* SEARCH */}
              <div className="relative mb-2">
                <input
                  type="text"
                  placeholder="Search users by name..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="w-full px-2.5 py-1 text-[11px] bg-slate-50 border border-slate-200 rounded-lg outline-none focus:bg-white focus:border-blue-500 transition-all text-slate-800 placeholder:text-slate-400"
                />
              </div>

              {/* USER LIST */}
              <div className="max-h-[160px] overflow-y-auto space-y-1 pr-1">
                {filteredUsers.map((u: any) => (
                  <div
                    key={u.ID}
                    className="flex items-center justify-between py-1 px-2 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar
                        size={26}
                        className="bg-slate-200 text-slate-600 text-[10px] font-semibold flex-shrink-0"
                      >
                        {(u.FullName || u.EmployeeCode || 'U')[0]?.toUpperCase()}
                      </Avatar>
                      <div className="text-[11px] font-medium text-slate-800 truncate">
                        {u.FullName || u.EmployeeCode || u.ID}
                      </div>
                    </div>

                    <button
                      onClick={() => handleAddMember(u.ID)}
                      disabled={addingMemberId === u.ID}
                      className={`text-[10px] font-medium px-2.5 py-1 rounded-md border-none transition-colors cursor-pointer ${addingMemberId === u.ID
                        ? 'opacity-50 cursor-not-allowed text-slate-400 bg-slate-100'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                        }`}
                    >
                      {addingMemberId === u.ID ? '...' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-slate-100">
          <button
            onClick={() => setIsMemberModalOpen(false)}
            className="text-[11px] font-medium px-3.5 py-1 text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors border-none cursor-pointer"
          >
            Close
          </button>
        </div>
      </Modal>
      <Modal
        open={pdfViewerState.isOpen}
        onCancel={() => setPdfViewerState(prev => ({ ...prev, isOpen: false }))}
        width="50%"
        title={pdfViewerState.fileName}
        destroyOnClose
        footer={[
          <button
            key="download"
            className="px-4 py-2 bg-blue-500 text-white rounded"
            onClick={() => handleDownloadAttachment(pdfViewerState)}
          >
            Download
          </button>,
          <button
            key="close"
            className="px-4 py-2 ml-2 bg-gray-300 rounded"
            onClick={() =>
              setPdfViewerState(prev => ({ ...prev, isOpen: false }))
            }
          >
            Close
          </button>,
        ]}
        onOk={() => {
          URL.revokeObjectURL(pdfViewerState.pdfUrl || '');
          setPdfViewerState({
            id: null,
            isOpen: false,
            pdfUrl: null,
            fileName: '',
          });
        }}
      >
        <div className="w-full h-[60vh]">
          {pdfViewerState.pdfUrl && (
            <iframe
              src={`${pdfViewerState.pdfUrl}#toolbar=0`}
              title={pdfViewerState.fileName}
              width="100%"
              height="100%"
            ></iframe>
          )}
        </div>
      </Modal>
    </>
  );
}

export default Chat;
